import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { queryKeys } from '../../query/queryKeys';
import { strapiMapFormQuestionsToJenis } from './formQuestionMapJenis';

function jenisPengukuranOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const nama = String(row.nama_jenis ?? '').trim();
  const kode = String(row.kode_jenis ?? '').trim();
  return [nama, kode].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

type Props = {
  jwt: string | null;
  slug: string;
  selectedDocumentIds: string[];
  jenisOptions: DynamicEntityRow[];
  jenisLoading?: boolean;
  onMapped?: () => void;
};

export function FormQuestionMapJenisBar({
  jwt,
  slug,
  selectedDocumentIds,
  jenisOptions,
  jenisLoading = false,
  onMapped,
}: Props) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jenisIds, setJenisIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedJenis = useMemo(
    () => jenisOptions.filter((row) => jenisIds.includes(row.documentId)),
    [jenisOptions, jenisIds],
  );

  const mapMut = useMutation({
    mutationFn: async () => {
      if (!jwt) throw new Error('Sesi tidak valid.');
      return strapiMapFormQuestionsToJenis(jwt, {
        formQuestionDocumentIds: selectedDocumentIds,
        jenisPengukuranDocumentIds: jenisIds,
      });
    },
    onSuccess: async (result) => {
      setError(null);
      setSuccess(
        `${result.updated_count} pertanyaan dipetakan ke ${result.jenis_pengukuran_count} jenis pengukuran.`,
      );
      setDialogOpen(false);
      setJenisIds([]);
      onMapped?.();
      await Promise.all([
        qc.invalidateQueries({ queryKey: queryKeys.masterEntityListPrefix(jwt, slug) }),
        qc.invalidateQueries({ queryKey: queryKeys.masterEntityMini(jwt, 'form-questions') }),
      ]);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Gagal memetakan form ke jenis pengukuran.'),
  });

  if (!selectedDocumentIds.length && !success && !dialogOpen) return null;

  return (
    <Stack spacing={1} sx={{ mb: 2 }} data-testid="form-question-map-jenis-bar">
      {selectedDocumentIds.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setError(null);
              setSuccess(null);
              setDialogOpen(true);
            }}
            data-testid="form-question-map-jenis-open"
          >
            Update map form to jenis pengukuran
          </Button>
          <Typography variant="caption" color="text.secondary">
            {selectedDocumentIds.length} pertanyaan dipilih
          </Typography>
        </Stack>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (mapMut.isPending) return;
          setDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        data-testid="form-question-map-jenis-dialog"
      >
        <DialogTitle>Update map form to jenis pengukuran</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedDocumentIds.length} pertanyaan terpilih akan memakai jenis pengukuran yang dipilih
              (bisa lebih dari satu).
            </Typography>
            <Autocomplete
              multiple
              options={jenisOptions}
              value={selectedJenis}
              loading={jenisLoading}
              onChange={(_, rows) => setJenisIds(rows.map((row) => row.documentId))}
              getOptionLabel={(row) => jenisPengukuranOptionLabel(row)}
              isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
              renderInput={(params) => (
                <TextField {...params} label="Jenis pengukuran" size="small" fullWidth />
              )}
            />
            {error ? (
              <Alert severity="error" data-testid="form-question-map-jenis-error">
                {error}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={mapMut.isPending}>
            Batal
          </Button>
          <Button
            variant="contained"
            disabled={mapMut.isPending || selectedDocumentIds.length === 0 || jenisIds.length === 0}
            onClick={() => mapMut.mutate()}
            data-testid="form-question-map-jenis-submit"
          >
            {mapMut.isPending ? 'Menyimpan…' : 'Update map'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
